import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CandidateProfile } from '@/lib/types/candidate';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, StarOff } from 'lucide-react';
import {
  toggleFavoriteCandidate,
  isCandidateFavorite
} from '@/lib/utils/favorites';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@/components/ui/tooltip';
import EmptyState from '@/components/ui/empty.state';
import { useState, useEffect } from 'react';

interface CandidateListProps {
  filteredCandidates: CandidateProfile[];
  openCandidateDetails: (id: string) => void;
  openOfferModal: (candidate: CandidateProfile) => void;
  list?: boolean;
  limit?: number;
}

const getInitials = (name: string) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(n => n[0])
    .join('');
};

const getTopSkills = (skills: CandidateProfile['skills']) => {
  if (!skills) return [];
  return skills.slice(0, 3);
};

const getDaysSinceJoining = (createdAt: string) => {
  const createdDate = new Date(createdAt);
  const currentDate = new Date();
  const diffTime = Math.abs(currentDate.getTime() - createdDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export function CandidateList({
  filteredCandidates,
  openCandidateDetails,
  list = false,
  limit,
  openOfferModal
}: CandidateListProps) {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    // Initialize favorites from cookies
    const favoriteIds = filteredCandidates
      .filter(candidate => isCandidateFavorite(candidate.id))
      .map(candidate => candidate.id);
    setFavorites(favoriteIds);
  }, [filteredCandidates]);

  const handleFavoriteClick = (e: React.MouseEvent, candidateId: string) => {
    e.stopPropagation();
    const newFavorites = toggleFavoriteCandidate(candidateId);
    setFavorites(newFavorites);
  };

  const calculateExperience = (
    experiences: CandidateProfile['work_experiences']
  ) => {
    if (!experiences || experiences.length === 0) return 0;
    const totalMonths = experiences.reduce((acc, exp) => {
      const start = new Date(exp.start_date);
      const end = exp.is_current ? new Date() : new Date(exp.end_date || '');
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
      return acc + months;
    }, 0);
    return Math.round(totalMonths / 12);
  };

  if (filteredCandidates.length === 0) {
    return (
      <EmptyState
        title="No candidates found"
        description="No candidates found, try changing search"
        renderButton={false}
      />
    );
  }

  return (
    <div
      data-cy="candidate-list"
      className={`grid gap-4 ${
        list ? 'grid-cols-1' : ' grid-cols-1 md:grid-cols-2'
      }`}
    >
      {filteredCandidates.slice(0, limit).map(candidate => (
        <Card
          key={candidate.id}
          className="p-4 group transition-transform duration-300 ease-out hover:shadow-sm cursor-pointer shadow-none"
          data-cy="candidate-card"
          onClick={() => openCandidateDetails(candidate.id)}
        >
          {/* Card Content */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={`https://www.cruit-europe.com/avatar5.png`}
                  alt={candidate.candidate_name}
                />
                <AvatarFallback>
                  {getInitials(candidate.candidate_name || candidate.email)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-sm" data-cy="candidate-name">
                  {getInitials(candidate.candidate_name || candidate.email)}
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={e => handleFavoriteClick(e, candidate.id)}
                      className="h-8 w-8"
                    >
                      {favorites.includes(candidate.id) ? (
                        <Star className="h-4 w-4 text-yellow-400" />
                      ) : (
                        <StarOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {favorites.includes(candidate.id)
                      ? 'Remove from favorites'
                      : 'Add to favorites'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                onClick={e => {
                  e.stopPropagation();
                  openCandidateDetails(candidate.id);
                }}
                size="sm"
                variant="outline"
              >
                View profile
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-500 line-clamp-2">
              {candidate.bio || 'No bio available'}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              joined {getDaysSinceJoining(candidate.created_at)} days ago
            </p>
          </div>
          <div className="mt-2 flex justify-between items-end">
            <div className="w-[70%]">
              <div className="flex gap-1 overflow-x-auto pb-1 max-w-full">
                {getTopSkills(candidate.skills).map((skill, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="whitespace-nowrap flex-shrink-0 text-xs"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-right w-[30%]">
              {candidate.overallScore !== undefined && (
                <>
                  <p className="text-xs font-medium mb-1 text-gray-400">
                    Overall score
                  </p>
                  <div className="flex gap-1 justify-end mb-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              candidate.overallScore >= 80 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : candidate.overallScore >= 75
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : candidate.overallScore >= 70
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : candidate.overallScore >= 50
                                ? 'bg-orange-50 text-orange-700 border-orange-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                            }`}
                          >
                            {candidate.overallScore}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-[300px] text-left space-y-1 p-2"
                        >
                          <p className="text-base font-medium text-gray-900">
                            Assessment Score
                          </p>
                          <p className="text-sm text-gray-600">
                            {candidate.overallScore >= 80 
                              ? 'Exceptional candidate - strongly recommended for hire'
                              : candidate.overallScore >= 75
                              ? 'Strong candidate - meets all requirements'
                              : candidate.overallScore >= 70
                              ? 'Good candidate - shows strong potential'
                              : candidate.overallScore >= 50
                              ? 'Promising candidate - may need some development'
                              : 'Needs improvement - requires significant training'}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Score based on: Experience (35%), Communication (25%), Coding (40%)
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </>
              )}
              <p className="text-[10px] text-gray-300 font-mono">
                ID: {candidate.id}
              </p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
